import React, { useState } from 'react';
import { useCollection } from '../../hooks/useCollection';
import { collection, addDoc, doc, deleteDoc, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import type { Invite } from '../../types';
import { useToast } from '../../hooks/useToast';
import ConfirmModal from '../../components/ConfirmModal';

const AdminInvitesPage: React.FC = () => {
    const { data: invites, setData: setInvites, isLoading } = useCollection<Invite>('invites');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<'admin' | 'developer'>('admin');
    const [isSending, setIsSending] = useState(false);
    const { showToast } = useToast();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inviteToDelete, setInviteToDelete] = useState<Invite | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSending(true);

        try {
            // Check if email already exists in users or invites
            const usersRef = collection(db, 'users');
            const userQuery = query(usersRef, where('email', '==', email.trim()), limit(1));
            const userSnapshot = await getDocs(userQuery);
            if (!userSnapshot.empty) {
                showToast('An active user with this email already exists.', { type: 'error' });
                setIsSending(false);
                return;
            }

            const invitesRef = collection(db, 'invites');
            const inviteQuery = query(invitesRef, where('email', '==', email.trim()), limit(1));
            const inviteSnapshot = await getDocs(inviteQuery);
            if (!inviteSnapshot.empty) {
                showToast('An invitation for this email already exists.', { type: 'error' });
                setIsSending(false);
                return;
            }

            // Add new invite
            const newInvite = { email: email.trim(), role };
            const docRef = await addDoc(invitesRef, newInvite);
            setInvites(prev => [...prev, { id: docRef.id, ...newInvite }]);
            showToast('Invitation sent successfully!', { type: 'success' });
            setEmail('');
            setRole('admin');
        } catch (error) {
            console.error(error);
            showToast('Failed to send invitation.', { type: 'error' });
        } finally {
            setIsSending(false);
        }
    };

    const openDeleteModal = (invite: Invite) => {
        setInviteToDelete(invite);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!inviteToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'invites', inviteToDelete.id));
            setInvites(prev => prev.filter(inv => inv.id !== inviteToDelete.id));
            showToast('Invitation deleted.', { type: 'success' });
        } catch (error) {
            showToast('Failed to delete invitation.', { type: 'error' });
        } finally {
            setIsDeleting(false);
            setIsModalOpen(false);
        }
    };

    return (
        <div>
            <ConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Invitation"
                description={`Are you sure you want to delete the invitation for ${inviteToDelete?.email}?`}
                isConfirming={isDeleting}
            />
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Manage User Invitations</h2>

            <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Invite New User</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                            placeholder="user@example.com"
                        />
                    </div>
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                        <select
                            id="role"
                            value={role}
                            onChange={e => setRole(e.target.value as 'admin' | 'developer')}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                        >
                            <option value="admin">Admin</option>
                            <option value="developer">Developer</option>
                        </select>
                    </div>
                    <div className="text-right">
                        <button
                            type="submit"
                            disabled={isSending}
                            className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400"
                        >
                            {isSending ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <h3 className="text-xl font-semibold text-gray-800 p-6">Pending Invitations</h3>
                {isLoading && <p className="p-6 text-center">Loading invitations...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(invites || []).map(invite => (
                                <tr key={invite.id}>
                                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 font-semibold">{invite.email}</p>
                                    </td>
                                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invite.role === 'developer' ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'}`}>
                                            {invite.role}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-right">
                                        <button onClick={() => openDeleteModal(invite)} className="text-red-600 hover:text-red-900">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {!isLoading && invites?.length === 0 && (
                    <p className="text-center py-10 text-gray-500">No pending invitations.</p>
                )}
            </div>
        </div>
    );
};

export default AdminInvitesPage;
